import React from 'react';
import {
    Show,
    SimpleShowLayout
} from 'react-admin';
import {
    Card,
    CardHeader,
    Typography,
    Grid,
    Paper,
    Divider
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import { connect } from 'react-redux';
import {makeStyles} from "@material-ui/styles";
import DripCampaignMetricBox from './DripCampaignMetricBox';
import DripCampaignRatesRow from './DripCampaignRatesRow';

const useStyles = makeStyles(theme => ({
    flex: {
        display: 'flex',
        paddingBottom: '12px',
    },
    divider: {
        marginBottom: '24px',
        marginTop: '24px',
    },
    link: {
        color: '#b24d43',
        textDecoration: 'none',
    },
    typographyTitle: {
        fontWeight: 'bold',
    },
    typographyContent: {
        paddingLeft: '5px',
    },
}));

const DripCampaignMetrics = ({ record, classes, t }) => {
    // Calculate success rate using sent vs failed enrollments
    const calculateSuccessRate = () => {
        if (!record.totalEnrollments || record.totalEnrollments === 0) return 100;
        const successful = record.sentEnrollments || 0;
        const total = successful + (record.failedEnrollments || 0);
        if (total === 0) return 100;
        return Math.round((successful / total) * 100);
    };

    return (
        <React.Fragment>
            <Grid container spacing={24}>
                <Grid item xs={12}>
                    <div className={classes.flex} style={{paddingBottom: '0px'}}>
                        <Typography noWrap variant={'title'} color={'primary'}>
                            {record.totalEnrollments || 0}
                        </Typography>
                        <Typography noWrap variant={'title'} className={classes.typographyContent}>
                            {t('drip.total_enrollments')}
                        </Typography>
                    </div>
                    <Typography variant={'caption'}>
                        {record.failedEnrollments === 0 && t('No enrollments failed. Success rate: 100%')}
                        {record.failedEnrollments > 0 &&
                        <React.Fragment>
                            <Typography component="span" color="primary">
                                {`${record.failedEnrollments || 0} `}
                            </Typography>
                            {t('enrollments failed. Success rate: %{successRate}%', {
                                successRate: calculateSuccessRate()
                            })}
                        </React.Fragment>
                        }
                    </Typography>
                    {/* Add scheduling info if available */}
                    {(record.lastEnrolledAt || record.nextScheduledAt) && (
                        <Typography variant={'caption'} display="block" style={{marginTop: '8px'}}>
                            {record.lastEnrolledAt && t('drip.last_enrolled_at', { date: new Date(record.lastEnrolledAt).toLocaleString() })}
                            {record.lastEnrolledAt && record.nextScheduledAt && ' • '}
                            {record.nextScheduledAt && t('drip.next_scheduled_at', { date: new Date(record.nextScheduledAt).toLocaleString() })}
                        </Typography>
                    )}
                    <Divider className={classes.divider}/>
                    <Grid container spacing={24}>
                        <Grid item xs={6}>
                            <div className={classes.flex}>
                                <Typography noWrap variant={'caption'} className={classes.typographyTitle}>
                                    {t('drip.name')}:
                                </Typography>
                                <Typography noWrap variant={'caption'} className={classes.typographyContent}>
                                    {record.name}
                                </Typography>
                            </div>
                            <div className={classes.flex}>
                                <Typography noWrap variant={'caption'} className={classes.typographyTitle}>
                                    {t('drip.base_campaign')}:
                                </Typography>
                                <Typography noWrap variant={'caption'} className={classes.typographyContent}>
                                    {record.messageName || '-'}
                                </Typography>
                            </div>
                            <div className={classes.flex}>
                                <Typography noWrap variant={'caption'} className={classes.typographyTitle}>
                                    {t('drip.target_group')}:
                                </Typography>
                                <Typography noWrap variant={'caption'} className={classes.typographyContent}>
                                    {record.targetGroupName || '-'}
                                </Typography>
                            </div>
                        </Grid>
                        <Grid item xs={6}>
                            <div className={classes.flex}>
                                <Typography noWrap variant={'caption'} className={classes.typographyTitle}>
                                    {t('drip.delay_in_hours')}:
                                </Typography>
                                <Typography noWrap variant={'caption'} className={classes.typographyContent}>
                                    {`${record.delayInHours || 0} hours`}
                                </Typography>
                            </div>
                            <div className={classes.flex}>
                                <Typography noWrap variant={'caption'} className={classes.typographyTitle}>
                                    {t('drip.trigger_type')}:
                                </Typography>
                                <Typography noWrap variant={'caption'} className={classes.typographyContent}>
                                    {record.triggerType ? t(`drip.trigger.${record.triggerType}`) : record.triggerType}
                                </Typography>
                            </div>
                            <div className={classes.flex}>
                                <Typography noWrap variant={'caption'} className={classes.typographyTitle}>
                                    {t('drip.status')}:
                                </Typography>
                                <Typography noWrap variant={'caption'} className={classes.typographyContent}>
                                    {record.isActive ? t('common.active') : t('common.inactive')}
                                </Typography>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Divider variant={'middle'} className={classes.divider}/>
            
            {/* Performance Rates Row */}
            <DripCampaignRatesRow record={record} />
            <Divider variant={'middle'} className={classes.divider}/>

            {/* Enrollment Statistics Row - First Row */}
            <Grid container spacing={0}>
                <Grid item xs={3}>
                    <DripCampaignMetricBox
                        dripCampaignId={record.id}
                        label={t('drip.total')}
                        type={'total'}
                        value={record.totalEnrollments || 0}
                    />
                </Grid>
                <Grid item xs={3}>
                    <DripCampaignMetricBox
                        dripCampaignId={record.id}
                        label={t('drip.sent')}
                        type={'sent'}
                        value={record.sentEnrollments || 0}
                    />
                </Grid>
                <Grid item xs={3}>
                    <DripCampaignMetricBox
                        dripCampaignId={record.id}
                        label={t('drip.queued')}
                        type={'queued'}
                        value={record.queuedEnrollments || 0}
                    />
                </Grid>
                <Grid item xs={3}>
                    <DripCampaignMetricBox
                        dripCampaignId={record.id}
                        label={t('drip.failed')}
                        type={'failed'}
                        value={record.failedEnrollments || 0}
                    />
                </Grid>
            </Grid>

            {/* Enrollment Statistics Row - Second Row */}
            {(record.cancelledEnrollments > 0 || record.skippedEnrollments > 0) && (
                <React.Fragment>
                    <Divider variant={'middle'} className={classes.divider} style={{marginTop: '16px', marginBottom: '16px'}}/>
                    <Grid container spacing={0}>
                        <Grid item xs={3}>
                            <DripCampaignMetricBox
                                dripCampaignId={record.id}
                                label={t('drip.cancelled')}
                                type={'cancelled'}
                                value={record.cancelledEnrollments || 0}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <DripCampaignMetricBox
                                dripCampaignId={record.id}
                                label={t('drip.skipped')}
                                type={'skipped'}
                                value={record.skippedEnrollments || 0}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <DripCampaignMetricBox
                                dripCampaignId={record.id}
                                label={t('drip.sent_rate')}
                                type={'rate'}
                                value={`${(record.sentRate || 0).toFixed(1)}%`}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <DripCampaignMetricBox
                                dripCampaignId={record.id}
                                label={t('drip.failure_rate')}
                                type={'rate'}
                                value={`${(record.failureRate || 0).toFixed(1)}%`}
                            />
                        </Grid>
                    </Grid>
                </React.Fragment>
            )}

        </React.Fragment>
    );
};

const ConnectedCustomShow = connect((state, props) => {
    const resourceState = state.admin.resources['drip-campaigns'];
    const record = resourceState && resourceState.data && resourceState.data[props.id];
    return { record };
})(({ record, t }) => {
    const classes = useStyles();
    
    if (!record) {
        return <div>Loading...</div>;
    }

    return (
        <React.Fragment>
            <Card>
                <CardHeader title={t('drip.report_title', {name: record.name})}/>
                <Paper style={{padding: '24px'}}>
                    <DripCampaignMetrics record={record} classes={classes} t={t} />
                </Paper>
            </Card>
        </React.Fragment>
    );
});

const DripCampaignShow = (props) => {
    const {t} = useTranslation();

    return (
        <Show {...props} title={t('drip.show_title')}>
            <SimpleShowLayout>
                <ConnectedCustomShow id={props.id} t={t} />
            </SimpleShowLayout>
        </Show>
    );
};

export default DripCampaignShow;